/**
 * Generic log viewer mainly targeted for use in internal tools.
 */
import type { MutableRefObject } from "react";
import { useRef, useState } from "react";
import styles from "./DebugLogViewer.module.scss";

enum LogMessageType {
  ERROR,
  INFO,
}

export interface DebugLogMessage {
  type: LogMessageType;
  message: string;
}

export function useDebugLogViewer() {
  const logMessages = useRef<DebugLogMessage[]>([]);
  const [errorMessagesGeneration, setLogMessagesGeneration] = useState<number>(0);
  const logElementRef = useRef<HTMLDivElement | null>(null);

  function refreshLog() {
    setTimeout(() => {
      // This needs to happen after React re-renders
      if (logElementRef.current) {
        logElementRef.current.scrollTop = logElementRef.current.scrollHeight;
      }
    }, 0);
    setLogMessagesGeneration(errorMessagesGeneration + 1);
  }

  function addErrorMessage(msg: string) {
    logMessages.current.push({ type: LogMessageType.ERROR, message: msg });
    refreshLog();
  }

  function addLogMessage(msg: string) {
    logMessages.current.push({ type: LogMessageType.INFO, message: msg });
    refreshLog();
  }

  return {
    addErrorMessage,
    addLogMessage,
    logElementRef,
    logMessages,
  };
}

interface DebugLogViewerProps {
  logMessages: MutableRefObject<DebugLogMessage[]>;
  logElementRef: MutableRefObject<HTMLDivElement | null>;
}

export default function DebugLogViewer({ logMessages, logElementRef }: DebugLogViewerProps) {
  return (
    <div className={styles.wrapper} ref={logElementRef}>
      {logMessages.current.map((msg, i) => (
        <p key={i} className={msg.type === LogMessageType.ERROR ? styles.error : ""}>
          {msg.message}
        </p>
      ))}
    </div>
  );
}
